import { Chip } from "@mui/material";
import {
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TopToolbar,
  type RaRecord,
} from "react-admin";
import DisplayDateField from "@/components/DisplayDateField";
import { ExportButton } from "@/components/ExportButton";
import { exportToExcel } from "@/utils/export-to-excel";

import { buildMiqaatAttendanceExportColumns } from "./export-miqaat-attendance";
import {
  formatAttendanceAffiliation,
  formatAttendanceAttendeeLabel,
  formatAttendanceChannel,
  formatAttendanceMatchSource,
  formatCheckedInBy,
  formatOptionalText,
} from "./miqaat-attendance-display";

const filters = [
  <ReferenceInput key="miqaatId" source="miqaatId" reference="miqaats" alwaysOn perPage={100}>
    <SelectInput optionText="name" label="Miqaat" resettable />
  </ReferenceInput>,
  <ReferenceInput key="venueId" source="venueId" reference="miqaatVenues" alwaysOn perPage={100}>
    <SelectInput optionText="name" label="Venue" resettable />
  </ReferenceInput>,
  <SelectInput
    key="rsvpOnFile"
    source="rsvpOnFile"
    label="RSVP Status"
    choices={[
      { id: "true", name: "With RSVP" },
      { id: "false", name: "Without RSVP (Walk-in)" },
    ]}
    resettable
  />,
  <SelectInput
    key="channel"
    source="channel"
    label="Channel"
    choices={[
      { id: "ADMIN", name: "Admin Scan" },
      { id: "WHATSAPP_SELF", name: "WhatsApp Self-Scan" },
    ]}
    resettable
  />,
];

const AttendanceListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton maxResults={10000} />
  </TopToolbar>
);

const attendanceExporter = (records: RaRecord[]) => {
  const columns = buildMiqaatAttendanceExportColumns();
  exportToExcel(columns, records as Record<string, unknown>[], {
    filenamePrefix: "miqaat_attendance",
    sheetName: "Attendance",
  });
};

export default function MiqaatAttendanceList() {
  return (
    <List
      filters={filters}
      actions={<AttendanceListActions />}
      exporter={attendanceExporter}
      sort={{ field: "createdAt", order: "DESC" }}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <ReferenceField source="miqaatId" reference="miqaats" link="show">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="venueId" reference="miqaatVenues" emptyText="—">
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          label="Attendee"
          render={(record: Record<string, unknown>) => formatAttendanceAttendeeLabel(record)}
        />
        <FunctionField
          label="RSVP Status"
          render={(record: { rsvpOnFile?: unknown }) => {
            const hasRsvp = Boolean(record?.rsvpOnFile);
            return (
              <Chip
                size="small"
                label={hasRsvp ? "RSVP Yes" : "Walk-in"}
                color={hasRsvp ? "success" : "warning"}
                variant={hasRsvp ? "filled" : "outlined"}
                sx={{ fontWeight: 600 }}
              />
            );
          }}
        />
        <FunctionField
          label="Affiliation"
          render={(record: Record<string, unknown>) => {
            const { label, isSabil } = formatAttendanceAffiliation(record);
            return (
              <Chip
                size="small"
                label={label}
                color={isSabil ? "primary" : "secondary"}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            );
          }}
        />
        <FunctionField
          label="Channel"
          render={(record: { channel?: unknown }) => formatAttendanceChannel(record?.channel)}
        />
        <FunctionField
          label="Match"
          render={(record: { matchSource?: unknown }) =>
            formatAttendanceMatchSource(record?.matchSource)
          }
        />
        <FunctionField
          label="Checked in by"
          render={(record: Record<string, unknown>) => formatCheckedInBy(record)}
        />
        <FunctionField
          label="Phone"
          render={(record: { phone?: unknown }) => formatOptionalText(record?.phone)}
        />
        <FunctionField
          label="Jamaat / Mohalla"
          render={(record: { jamaatMohalla?: unknown }) =>
            formatOptionalText(record?.jamaatMohalla)
          }
        />
        <DisplayDateField source="createdAt" showTime />
      </Datagrid>
    </List>
  );
}
